import { FC, Fragment } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Types
import CaseTypes from 'app/types/CaseTypes';
// Selectors
import { selectTeamsAsOptions } from 'app/store/Teams/Teams.selectors';
import { selectAccountsAsOptions } from 'app/store/Clients/Clients.selectors';
// Mui
import { Box, Divider, Typography, FormHelperText, TextField, Autocomplete as MuiAutocomplete } from '@mui/material';
// Components
import { Input, DesktopDatePicker, DesktopTimePicker } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
// Utility
import { isRequired, isDateValid, isTimeValid, isMaxLength } from 'app/utilities/Validations';

interface IFormData {
  name: string;
  type: string;
  teamId: string | number;
  clientAccountId?: string | number;
  eventDate: Dayjs | null,
  eventTime: Dayjs | null;
  settings: {
    aiChatEnabled: boolean;
  }
}

const GeneralStepContent:FC = () => {
  const { t } = useTranslation();

  // State
  const teamOptions = useAppSelector(selectTeamsAsOptions);
  const accountOptions = useAppSelector(selectAccountsAsOptions);

  const { control, formState: { errors }, watch } = useFormContext<IFormData>();

  const watchEventDate= watch('eventDate');

  return (
    <Fragment>
      <Controller
        control={control} name="name"
        rules={{
          required: isRequired,
          maxLength: isMaxLength(100)
        }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('labels.caseName')}
            required
            error={Boolean(errors.name)}
            helperText={errors.name?.message || ''}
            slotProps={{
              input: {
                endAdornment: (
                  <FormHelperText
                    sx={{ p: 0, whiteSpace: 'nowrap' }}
                  >{`${watch('name').length} / 100`}</FormHelperText>
                )
              }
            }}
          />
        )}
      />
      <Controller
        control={control} name="type"
        rules={{
          maxLength: isMaxLength(100)
        }}
        render={({ field: { value, onChange } }) => (
          <MuiAutocomplete
            freeSolo
            value={value ?? ''}
            onChange={(_, value) => onChange(value)}
            onInputChange={(_, value) => onChange(value)}
            options={(Object.keys(CaseTypes) as Array<keyof typeof CaseTypes>).map(key => CaseTypes[key])}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('labels.type')}
                error={Boolean(errors.type)}
                helperText={errors.type?.message || ''}
              />
            )}
          />
        )}
      />
      <Box sx={{ display: 'flex', gap: 4 }} >
        <Controller
          control={control} name="eventDate"
          rules={{
            required: isRequired,
            validate: isDateValid
          }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              label={t('labels.eventDate')}
              slotProps={{
                textField: {
                  required: true,
                  error: Boolean(errors.eventDate),
                  helperText: errors.eventDate?.message || ''
                }
              }}
            />
          )}
        />
        {watchEventDate ? (
          <Controller
            control={control} name="eventTime"
            rules={{
              validate: {
                pattern: isTimeValid
              }
            }}
            render={({ field }) => (
              <DesktopTimePicker
                {...field}
                label={t('labels.eventTime')}
                slotProps={{
                  textField: {
                    error: Boolean(errors.eventTime),
                    helperText: errors.eventTime?.message || ''
                  }
                }}
              />
            )}
          />
        ) : null}
      </Box>
      <Divider sx={{ mt: 4, mb: 2 }} />
      <Controller
        control={control} name="teamId"
        render={({ field }) => (
          <Autocomplete
            {...field}
            options={teamOptions || []}
            TextFieldProps={{
              label: t('labels.team')
            }}
            renderOption={(props, option) => (
              <li {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} key={option.value}>
                <Typography variant="subtitle1">{option.label}</Typography>
                {option.props.description ? (
                  <Typography variant="caption">{option.props.description}</Typography>
                ) : null}
              </li>
            )}
          />
        )}
      />
      {/* Client account */}
      <Controller
        control={control} name="clientAccountId"
        render={({ field }) => (
          <Autocomplete
            {...field}
            options={accountOptions || []}
            TextFieldProps={{
              label: t('labels.clientAccount')
            }}
            renderOption={(props, option) => (
              <li {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} key={option.value}>
                <Typography variant="subtitle1">{option.label}</Typography>
                {option.props.address ? (
                  <Typography variant="caption">{option.props.address}</Typography>
                ) : null}
              </li>
            )}
            disableClearable={true}
          />
        )}
      />
    </Fragment>
  );
};

export default GeneralStepContent;